import { z } from "zod";
import { IsoDateTimeSchema, MoneySchema, PriceStatusSchema, QuantitySchema, BudgetVerdictSchema } from "./primitives.js";
/**
 * Pre-beta program WS3 (Shop → Pantry v1): the minimum truthful purchase
 * confirmation. A row is `bought` (the confirmed quantity entered the pantry),
 * `already_had` (the user owned it; the typed quantity, if any, entered the
 * pantry) or `not_bought` (nothing entered the pantry; the row stays open).
 * Only confirmed quantities ever become inventory — the row's
 * `expectedLeftover` (Pantry Carryover) stays a forecast.
 */
export const PurchaseStateSchema = z.enum(["bought", "already_had", "not_bought"]);
export const ShoppingItemPurchaseSchema = z.object({
    state: PurchaseStateSchema,
    /** The confirmed quantity in the ingredient's canonical unit (0 for not_bought and for manual rows). */
    quantity: QuantitySchema.nullable(),
    confirmedAt: IsoDateTimeSchema,
});
/**
 * Shop list clarity (2026-10-06): the curated SUPERMARKET SECTION of a shopping
 * row, in DISPLAY ORDER (clients render the groups in this order, headers only for
 * non-empty groups). The wire value is a stable key; the German/English labels live
 * in packages/i18n (`views.shop.section.*`). Mirrors `services/catalog` `SHOPPING_SECTIONS`
 * (the catalog package has no dependency on contracts — a test pins the two equal).
 */
export const SHOPPING_SECTIONS = ["produce", "chilled", "meat_fish", "frozen", "dry_goods", "spices_oils", "drinks", "other"];
export const ShoppingSectionSchema = z.enum(SHOPPING_SECTIONS);
/** What ONE counted unit of a count-canonical ingredient is called ("10 cloves", "9 pieces") — curated in the catalog, words in packages/i18n. */
export const CountNounSchema = z.enum(["piece", "clove"]);
/** What a multi-unit PACK of a count-canonical ingredient is called ("1 bulb (10 cloves)", "1 net (9 pieces)"); absent = sold loose. */
export const PackNounSchema = z.enum(["bulb", "net", "pack"]);
// Manual rows (user-typed items) legitimately carry nulls for calculated
// fields — the migration allows them and fabricating zeros would violate the
// truthful-data rule. Calculated rows populate everything.
const ShoppingListItemObjectSchema = z.object({
    id: z.string(),
    canonicalIngredientId: z.string().nullable(),
    displayName: z.string(),
    requiredQuantity: QuantitySchema.nullable(),
    pantryReserved: QuantitySchema.nullable(),
    netNeeded: QuantitySchema.nullable(),
    selectedPack: z
        .object({
        quantity: z.number(),
        unit: z.string(),
        priceEstimate: MoneySchema,
    })
        .nullable(),
    buyQuantity: z.number().int().nonnegative().nullable(),
    expectedLeftover: QuantitySchema.nullable(),
    sourceMealIds: z.array(z.string()),
    manual: z.boolean(),
    checked: z.boolean(),
    priceEstimate: MoneySchema.nullable(),
    /** WS3: null until the user confirms what came home from the shop. */
    purchase: ShoppingItemPurchaseSchema.nullable().optional(),
    /**
     * Shop list clarity (2026-10-06): presentation facts DERIVED FROM THE CATALOG at read
     * time (never persisted on the row, no quantity or price depends on them). Absent on
     * manual rows, on rows whose ingredient is not curated yet (production before the next
     * catalog load) and from an older server — clients then show the row under "Sonstiges"
     * and fall back to the plain "N×" wording.
     *
     * EXCEPTION to this schema's strictness (one line): these three are display-only hints, so an unknown
     * value from a newer server degrades the row to absent and must never fail the list (`dropUnknownDisplayHints`).
     */
    section: ShoppingSectionSchema.optional(),
    /** The word for one counted unit of this row's `count` quantity (cloves, pieces). */
    countNoun: CountNounSchema.optional(),
    /** The word for a pack of this row when a pack holds several counted units (bulb, net, pack). */
    packNoun: PackNounSchema.optional(),
}).superRefine((item, ctx) => {
    if (!item.manual) {
        for (const [k, v] of Object.entries({
            canonicalIngredientId: item.canonicalIngredientId,
            requiredQuantity: item.requiredQuantity,
            pantryReserved: item.pantryReserved,
            netNeeded: item.netNeeded,
            buyQuantity: item.buyQuantity,
            expectedLeftover: item.expectedLeftover,
            priceEstimate: item.priceEstimate,
        })) {
            if (v === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `calculated row must populate ${k}`,
                    path: [k],
                });
            }
        }
    }
});
const DISPLAY_HINTS = { section: ShoppingSectionSchema, countNoun: CountNounSchema, packNoun: PackNounSchema };
/**
 * The three display-only hints (`section`, `countNoun`, `packNoun`) are the one tolerant spot in this otherwise strict schema: a value
 * outside the closed set — or of the wrong type — from a NEWER server is dropped before validation, so the row degrades (clients fall back
 * to "Sonstiges" and the plain "N×") and the list never fails. An absent key stays absent.
 */
function dropUnknownDisplayHints(input) {
    if (input === null || typeof input !== "object" || Array.isArray(input))
        return input;
    const row = input;
    let out;
    for (const [key, schema] of Object.entries(DISPLAY_HINTS)) {
        if (key in row && !schema.safeParse(row[key]).success) {
            out ??= { ...row };
            delete out[key];
        }
    }
    return out ?? input;
}
export const ShoppingListItemSchema = z.preprocess(dropUnknownDisplayHints, ShoppingListItemObjectSchema);
// GET /v1/shopping-lists/current response
export const ShoppingListResponseSchema = z.object({
    id: z.string(),
    planRevision: z.number().int().nonnegative(),
    revision: z.number().int().nonnegative(),
    currency: z.literal("EUR"),
    estimateStatus: PriceStatusSchema,
    items: z.array(ShoppingListItemSchema),
    /** The LIVE budget verdict (same cost model as generation/proposals/Fit
     * Score/Amy), recomputed against the CURRENT pantry and catalog prices —
     * a DIFFERENT dataset from the stored rows in `items` (which carry their
     * materialization-time prices). Clients must label it as a live estimate
     * and never headline it as the total of the visible rows (owner invariant
     * 2026-09-04: one dataset per presentation). */
    budget: BudgetVerdictSchema.optional(),
    /**
     * Owner invariant 2026-09-04: the pricing summary of the VISIBLE rows in
     * `items` (the stored list) — the ONLY subtotal a client may headline for
     * them, and the number the visible priced rows reconcile to exactly.
     * `complete` is ceiling-independent: false when any required row
     * (calculated without a price, or manual) lacks a reliable price.
     */
    pricing: z
        .object({
        complete: z.boolean(),
        /** Sum of the visible PRICED rows' price estimates (cents). Partial when !complete. */
        pricedSubtotalCents: z.number().int().nonnegative(),
        pricedRowCount: z.number().int().nonnegative(),
        /** Rows still needing a reliable price: calculated-unpriced + manual. */
        unpricedRequiredCount: z.number().int().nonnegative(),
        realPriceCount: z.number().int().nonnegative(),
        estimatedPriceCount: z.number().int().nonnegative(),
    })
        .optional(),
    unpricedCount: z
        .number()
        .int()
        .nonnegative()
        .describe("Calculated (non-manual) items whose price is honestly unavailable (Money status 'unavailable', C-043/ADR-028 — replaces the fake €0.00 rows). Clients render 'n items unpriced' and MUST exclude these rows from any basket total."),
    /**
     * WS3: present on the response of a purchase confirmation — how many
     * ingredients' stock the confirmation added to or corrected in the pantry.
     * Never present on a plain read.
     */
    pantrySync: z
        .object({ addedIngredients: z.number().int().nonnegative(), adjustedIngredients: z.number().int().nonnegative() })
        .optional(),
});
// POST /v1/shopping-lists/current/purchases request (WS3 Shop → Pantry v1)
export const ShoppingPurchaseItemInputSchema = z.object({
    itemId: z.string(),
    state: PurchaseStateSchema,
    /**
     * Confirmed quantity in the ingredient's canonical unit. Optional: a
     * `bought` row defaults to the packs on the list (buyQuantity × pack size),
     * `already_had` defaults to 0 (nothing typed = nothing entered), `not_bought`
     * is always 0. Never negative.
     */
    quantity: z.number().nonnegative().optional(),
});
export const ShoppingPurchaseRequestSchema = z
    .object({
    baseRevision: z.number().int().nonnegative(),
    items: z.array(ShoppingPurchaseItemInputSchema).optional(),
    /** "Shop done": every still-unresolved calculated row that needs buying defaults to `bought`; rows already resolved keep their state; the user may correct rows afterwards. */
    shopDone: z.boolean().optional(),
})
    .refine((body) => (body.items?.length ?? 0) > 0 || body.shopDone === true, { message: "items or shopDone required" });
// PATCH /v1/shopping-lists/current request
export const ManualShoppingItemInputSchema = z.object({
    displayName: z.string().min(1),
    quantity: QuantitySchema,
});
export const ShoppingListPatchRequestSchema = z.object({
    baseRevision: z.number().int().nonnegative(),
    addManualItems: z.array(ManualShoppingItemInputSchema).optional(),
    removeItemIds: z.array(z.string()).optional(),
    setCheckedItemIds: z.array(z.string()).optional(),
    setUncheckedItemIds: z.array(z.string()).optional(),
});
//# sourceMappingURL=shopping.js.map